// The arithmetic behind the Marriage register's summary. Pure functions over
// the banns and the certificates, so the screen only lays the answers out.

import { countBy, dateOf, inYear, lastMonths, upcoming, isoOf } from '../../utils/summaryStats';
import type { NamedCount, MonthBucket } from '../../utils/summaryStats';
import { liveByStatus } from '../../utils/records';

export interface BannLike {
  recordId?: string;
  status?: string;
  bannNo?: string;
  groomId?: string;
  groomName?: string;
  groomChurch?: string;
  brideId?: string;
  brideName?: string;
  brideChurch?: string;
  firstDate?: string;
  secondDate?: string;
  thirdDate?: string;
  officiatingPresbyter?: string;
  objections?: string;
  /** Pending / Approved / Objected / Cancelled — blank reads as Pending. */
  approvalStatus?: string;
}

export interface CertificateLike {
  recordId?: string;
  status?: string;
  registerNo?: string;
  certificateNo?: string;
  groomMemberId?: string;
  brideMemberId?: string;
  groomName?: string;
  brideName?: string;
  marriageDate?: string;
  marriagePlace?: string;
  minister?: string;
  witness1?: string;
  witness2?: string;
}

/** A banns publication falling in the coming weeks. */
export interface Reading {
  bann: BannLike;
  date: string;
  reading: 1 | 2 | 3;
}

export interface IncompleteCertificate {
  cert: CertificateLike;
  missing: string[];
}

export interface YearCount {
  year: string;
  count: number;
}

export interface MarriageSummary {
  banns: number;
  certificates: number;
  bannsByStatus: NamedCount[];
  pending: BannLike[];
  /** Banns read three times, not refused, with no certificate for the couple yet. */
  awaitingWedding: BannLike[];
  objected: BannLike[];
  readingsAhead: Reading[];
  interChurch: BannLike[];
  weddingsThisYear: number;
  weddingsLastYear: number;
  months: MonthBucket[];
  years: YearCount[];
  ministers: NamedCount[];
  /** Certificates where one spouse is not linked to a member of this parish. */
  outsideSpouse: CertificateLike[];
  incomplete: IncompleteCertificate[];
}

const txt = (v: unknown): string => String(v ?? '').trim();
const norm = (v: unknown): string => txt(v).toLowerCase().replace(/\s+/g, ' ');

export const bannStatus = (b: BannLike): string => txt(b.approvalStatus) || 'Pending';

const REFUSED = ['Objected', 'Cancelled'];

/** How many of the three publications have a date. */
export const readingsDone = (b: BannLike, now: Date = new Date()): number => {
  const today = isoOf(now);
  return [b.firstDate, b.secondDate, b.thirdDate].filter((d) => {
    const iso = dateOf(d);
    return iso !== null && iso <= today;
  }).length;
};

/** A certificate belongs to a bann when it names the same couple — by member id or by name. */
export const sameCouple = (b: BannLike, c: CertificateLike): boolean => {
  const byId = (a?: string, z?: string) => Boolean(txt(a)) && txt(a) === txt(z);
  const byName = (a?: string, z?: string) => Boolean(norm(a)) && norm(a) === norm(z);
  const groom = byId(b.groomId, c.groomMemberId) || byName(b.groomName, c.groomName);
  const bride = byId(b.brideId, c.brideMemberId) || byName(b.brideName, c.brideName);
  return groom && bride;
};

/** Churches named on both sides and not the same one. */
export const isInterChurch = (b: BannLike): boolean =>
  Boolean(norm(b.groomChurch)) && Boolean(norm(b.brideChurch)) && norm(b.groomChurch) !== norm(b.brideChurch);

export const marriageSummary = (
  bannRows: BannLike[], certRows: CertificateLike[], now: Date = new Date()
): MarriageSummary => {
  const banns = liveByStatus(bannRows);
  const certs = liveByStatus(certRows);
  const today = isoOf(now);
  const year = now.getFullYear();

  const readingsAhead: Reading[] = [];
  banns.filter((b) => !REFUSED.includes(bannStatus(b))).forEach((b) => {
    ([b.firstDate, b.secondDate, b.thirdDate] as Array<string | undefined>).forEach((d, i) => {
      if (upcoming([d], (x) => x, 30, now).length) {
        readingsAhead.push({ bann: b, date: dateOf(d) as string, reading: (i + 1) as 1 | 2 | 3 });
      }
    });
  });
  readingsAhead.sort((a, b) => a.date.localeCompare(b.date));

  const years = new Map<string, number>();
  certs.forEach((c) => {
    const d = dateOf(c.marriageDate);
    if (d) years.set(d.slice(0, 4), (years.get(d.slice(0, 4)) || 0) + 1);
  });

  return {
    banns: banns.length,
    certificates: certs.length,
    bannsByStatus: countBy(banns, bannStatus),
    pending: banns.filter((b) => bannStatus(b) === 'Pending')
      .sort((a, b) => txt(a.firstDate).localeCompare(txt(b.firstDate))),
    awaitingWedding: banns
      .filter((b) => !REFUSED.includes(bannStatus(b)))
      .filter((b) => { const d = dateOf(b.thirdDate); return d !== null && d <= today; })
      .filter((b) => !certs.some((c) => sameCouple(b, c)))
      .sort((a, b) => txt(a.thirdDate).localeCompare(txt(b.thirdDate))),
    objected: banns.filter((b) => REFUSED.includes(bannStatus(b))),
    readingsAhead,
    interChurch: banns.filter(isInterChurch),
    weddingsThisYear: inYear(certs, (c) => c.marriageDate, year).length,
    weddingsLastYear: inYear(certs, (c) => c.marriageDate, year - 1).length,
    months: lastMonths(certs.filter((c) => (dateOf(c.marriageDate) || '') <= today), (c) => c.marriageDate, 12, now),
    years: Array.from(years, ([y, count]) => ({ year: y, count }))
      .sort((a, b) => b.year.localeCompare(a.year))
      .slice(0, 10),
    ministers: countBy(certs, (c) => c.minister, 'Not recorded'),
    outsideSpouse: certs.filter((c) => !txt(c.groomMemberId) || !txt(c.brideMemberId)),
    incomplete: certs
      .map((cert) => ({
        cert,
        missing: [
          !txt(cert.registerNo) && 'Register No.',
          !txt(cert.certificateNo) && 'Certificate No.',
          (!txt(cert.witness1) || !txt(cert.witness2)) && 'Witnesses'
        ].filter(Boolean) as string[]
      }))
      .filter((x) => x.missing.length > 0)
      .sort((a, b) => txt(b.cert.marriageDate).localeCompare(txt(a.cert.marriageDate)))
  };
};
